import { Typography, useTheme } from '@mui/material';
import { Modal } from 'sharedComponents/Modal/Modal';
import { CloseButton } from '../../../../sharedComponents/Buttons';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useStateParam } from '../Context/ConnectStateCodeContextProvider';
import { getConnectUrlPath } from '../JobDiscovery/utils';
import auth from '../../../../utils/auth';
import { LoadingSpinner } from 'sharedComponents/LoadingSpinner/loadingSpinner';
import axios from 'axios';
import { ConnectRenewJobSeekingStatusPageDataTestIds } from '../../../../data-testids/ConnectDataTestIds';

interface RenewJobSeekingStatusPresentationProps {
  isLoading: boolean;
  hasError: boolean;
  title: string;
  onClose: () => void;
}

const RenewJobSeekingStatusPresentation: React.FC<RenewJobSeekingStatusPresentationProps> = ({
  isLoading,
  hasError,
  title,
  onClose,
}) => {
  const theme = useTheme();
  let content: React.ReactNode;

  if (isLoading) {
    content = <LoadingSpinner dataTestId={null} sx={{ margin: 8 }} />;
  } else if (hasError) {
    content = <>Something went wrong. Please try again in a few minutes.</>;
  } else {
    content = (
      <div data-testid={ConnectRenewJobSeekingStatusPageDataTestIds.BODY_TEXT}>
        Thanks for confirming you&apos;re still looking for a job.
        <br />
        <br />
        You&apos;ll stay visible to hiring principals and continue getting matched with great-fit
        schools.
        <br />
        <br />
        We&apos;ll ask you to renew again in 3 weeks - keeping your status fresh will help you get
        noticed by the right schools at the right time.
      </div>
    );
  }

  return (
    <Modal
      title={title}
      open
      closeButton={
        <CloseButton
          dataTestId={ConnectRenewJobSeekingStatusPageDataTestIds.CLOSE_MODAL_BUTTON}
          onClick={onClose}
        />
      }
      onClose={onClose}
      borderRadius={theme.shape.borderRadius}
    >
      <Typography variant="body1" mb={3} component="div">
        {content}
      </Typography>
    </Modal>
  );
};

const RenewJobSeekingStatus: React.FC = () => {
  const history = useHistory();
  const jobBoardState = useStateParam();
  const connectUrlPath = getConnectUrlPath(jobBoardState);
  const authenticatedUser = auth.getUser();
  const [isLoading, setIsLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [hasError, setHasError] = useState(false);

  const onClose = useCallback(() => {
    history.push(connectUrlPath);
  }, [history, connectUrlPath]);

  useEffect(() => {
    const updateStatus = async () => {
      try {
        await axios.post(`/api/preferences/${authenticatedUser.id}/renew_open_to_work/`);
        setTitle("You're all set!");
      } catch (e) {
        setHasError(true);
        setTitle('Something went wrong.');
      } finally {
        setIsLoading(false);
      }
    };

    updateStatus();
  }, [authenticatedUser.id, setHasError, setIsLoading, setTitle]);

  return (
    <RenewJobSeekingStatusPresentation
      isLoading={isLoading}
      hasError={hasError}
      title={title}
      onClose={onClose}
    />
  );
};

export default RenewJobSeekingStatus;
export { RenewJobSeekingStatusPresentation };
